import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer
} from '@tiptap/react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

export interface Attributes {
  isReadOnly: boolean;
  placeholder: string;
  value?: string;
}

const DateFieldView: Parameters<typeof ReactNodeViewRenderer>[0] = ({
  node,
  selected,
  updateAttributes
}) => {
  const { isReadOnly, placeholder, value } = node.attrs as Attributes;

  const triggerRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() =>
    value === undefined ? value : new Date(value)
  );

  useEffect(() => {
    if (!selected || !triggerRef.current) return;

    triggerRef.current.focus();
  }, [selected]);

  const onSelect = (next?: Date) => {
    setDate(next);
    updateAttributes({ value: next ? next.toISOString() : undefined });
    setOpen(false);
  };

  const label = date ? (
    <span>{format(date, 'yyyy-MM-dd')}</span>
  ) : (
    <span className="text-muted-foreground">{placeholder}</span>
  );

  return (
    <NodeViewWrapper className="inline-flex align-middle">
      <Popover
        onOpenChange={setOpen}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            className="inline-flex h-8! items-center gap-2 rounded-sm px-2! text-sm"
            disabled={isReadOnly}
            ref={triggerRef}
            tabIndex={-1}
            variant="outline"
          >
            <CalendarIcon className="size-4" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto p-0"
        >
          <Calendar
            autoFocus
            mode="single"
            onSelect={onSelect}
            selected={date}
          />
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};

const DateField = Node.create({
  addAttributes() {
    return {
      isReadOnly: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-read-only'),
        renderHTML: (attrs: Attributes) => ({
          'data-is-read-only': attrs.isReadOnly
        })
      },
      placeholder: {
        default: 'Pick a date'
      },
      value: {
        default: undefined,
        parseHTML: element => element.getAttribute('data-value'),
        renderHTML: (attributes: Attributes) => ({
          'data-value': attributes.value
        })
      }
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(DateFieldView);
  },
  atom: true,
  group: 'inline',
  inline: true,
  name: 'dateField',
  parseHTML() {
    return [{ tag: 'date-picker' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'date-picker',
      mergeAttributes(HTMLAttributes, { class: 'inline-flex align-middle' })
    ];
  }
});

export default DateField;
